import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

/**
 * The visitor's explicit choice. Absent means "follow the operating system".
 * index.html reads the same key in an inline script before first paint, so a
 * prerendered page never flashes the wrong theme — keep the two in step.
 */
export const THEME_KEY = 'tb_theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';
/** The browser chrome colour for each theme: the marketing page background. */
const THEME_COLOR: Record<Theme, string> = { light: '#fdfefa', dark: '#0f1a17' };

const listeners = new Set<() => void>();

function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme, animate: boolean) {
  const root = document.documentElement;
  // Colours cross-fade only on a deliberate switch, never on load or for reduced motion.
  if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    root.classList.add('theme-transition');
    window.setTimeout(() => root.classList.remove('theme-transition'), 300);
  }
  root.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  listeners.forEach(listener => listener());
}

/** Follows the OS setting live until the visitor makes a choice of their own. */
function followSystem() {
  if (!storedTheme()) applyTheme(systemTheme(), true);
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) {
    // The inline script sets this; it cannot when the page loaded without it running.
    if (!document.documentElement.dataset.theme) applyTheme(storedTheme() ?? systemTheme(), false);
    window.matchMedia(DARK_QUERY).addEventListener('change', followSystem);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0)
      window.matchMedia(DARK_QUERY).removeEventListener('change', followSystem);
  };
}

const getSnapshot = (): Theme =>
  document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
// The prerendered markup cannot know the theme: null until hydrated.
const getServerSnapshot = () => null;

function toggleTheme() {
  const next: Theme = getSnapshot() === 'dark' ? 'light' : 'dark';
  try {
    // Choosing the theme the OS already asks for returns to following the OS.
    if (next === systemTheme()) localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, next);
  } catch {
    // Storage blocked: the switch still applies for this page view.
  }
  applyTheme(next, true);
}

/**
 * The active theme (null while rendering on the server and during hydration)
 * and a toggle. The source of truth is <html data-theme>.
 */
export function useTheme() {
  const theme = useSyncExternalStore<Theme | null>(subscribe, getSnapshot, getServerSnapshot);
  return { theme, toggle: toggleTheme };
}
